import React, { useEffect } from "react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { GlobalSettingsProvider } from "./context/GlobalSettingsContext";

import AppRoutes from "./routes/AppRoutes";
import { ThemeProviderWrapper } from "./context/ThemeContext";
import { Provider } from "react-redux";
import { store } from "./shared/store/store";
import Toast from "./pages/components/toast/Toast";
import AOS from "aos";
import "aos/dist/aos.css";
import ErrorBoundary from "./shared/components/ErrorBoundary";

const App = () => {
  // Turn every data table into labelled cards on mobile and tablet screens.
  // The observer also covers tables that are rendered after an API response.
  useEffect(() => {
    const enhanceResponsiveTables = () => {
      document.querySelectorAll<HTMLTableElement>("table").forEach((table) => {
        const headerRows = table.querySelectorAll<HTMLTableRowElement>("thead tr");
        const headerRow = headerRows.item(headerRows.length - 1);

        if (!headerRow) return;

        const labels = Array.from(
          headerRow.querySelectorAll<HTMLTableCellElement>("th, td")
        ).map((cell) => cell.innerText.trim());

        if (!labels.some(Boolean)) return;

        table.classList.add("responsive-stacked-table");
        table
          .querySelectorAll<HTMLTableRowElement>("tbody tr")
          .forEach((row) => {
            Array.from(row.children).forEach((cell, index) => {
              if (!(cell instanceof HTMLTableCellElement)) return;

              if (cell.colSpan > 1) {
                cell.setAttribute("data-responsive-full-width", "true");
                cell.removeAttribute("data-label");
                return;
              }

              cell.setAttribute("data-label", labels[index] || "");
            });
          });
      });
    };

    let animationFrame = window.requestAnimationFrame(enhanceResponsiveTables);
    const observer = new MutationObserver(() => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(enhanceResponsiveTables);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(animationFrame);
    };
  }, []);

  // Initialize AOS
  useEffect(() => {
    AOS.init({
      duration: 2000,
      once: false,
      mirror: true,
    });
  }, []);

  // Language change detection and reload logic
  useEffect(() => {
    // Initialize language in localStorage if not present
    if (!localStorage.getItem("language")) {
      localStorage.setItem("language", "en");
    }

    let currentLanguage = localStorage.getItem("language") || "en";

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "language" && e.newValue !== currentLanguage) {
        currentLanguage = e.newValue || "en";
        // Reload the page when language changes
        window.location.reload();
      }
    };

    // Listen for storage events (when localStorage changes in other tabs/windows)
    window.addEventListener("storage", handleStorageChange);

    // Also listen for custom events (when language changes in the same tab)
    const handleLanguageChange = (e: CustomEvent) => {
      if (e.detail?.language !== currentLanguage) {
        currentLanguage = e.detail?.language || "en";
        // Reload the page when language changes
        window.location.reload();
      }
    };

    window.addEventListener(
      "languageChange",
      handleLanguageChange as EventListener
    );

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(
        "languageChange",
        handleLanguageChange as EventListener
      );
    };
  }, []);

  return (
    <ErrorBoundary>
      <Provider store={store}>
        <GlobalSettingsProvider>
          {/* <ThemeProviderWrapper> */}
          <AppRoutes />
          <Toast />
          {/* </ThemeProviderWrapper> */}
        </GlobalSettingsProvider>
      </Provider>
    </ErrorBoundary>
  );
};

export default App;
